import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { useRef, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthContext } from "@/auth/hooks/useAuthContext";
import { ContentSkeleton } from "@/components/PageLoading";
import { cn } from "@/lib/utils";

import { useGetAttendanceId } from "../../hook/useGetAttendanceId";
import { useSendCheckIn } from "../../hook/useSendCheckIn";

const FACE_HOLD_TIME = 1000;
const EYE_BLINK_THRESHOLD = 0.4; // blendshape score above this = eye closed

// Responsive dimensions
const getResponsiveDimensions = () => {
  const isMobile = window.innerWidth < 768;
  const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;

  if (isMobile) {
    // Full width on mobile with larger height for better face detection
    const videoWidth = window.innerWidth - 32; // Full width minus padding
    const videoHeight = Math.min(400, videoWidth * 1.2); // Increased height ratio
    return {
      videoWidth,
      videoHeight,
      circleRadius: Math.min(120, videoWidth * 0.3), // Larger circle for easier alignment
      circleCenter: {
        x: videoWidth / 2,
        y: videoHeight / 2,
      },
    };
  }
  if (isTablet) {
    return {
      videoWidth: 480,
      videoHeight: 360,
      circleRadius: 100,
      circleCenter: { x: 240, y: 180 },
    };
  }
  // Desktop - fixed dimensions
  return {
    videoWidth: 480,
    videoHeight: 360,
    circleRadius: 120,
    circleCenter: { x: 240, y: 180 },
  };
};

const LIVENESS_STEPS = [
  "Boshni chapga buring",
  "Boshni o'ngga buring",
  "Boshni to'g'ri qilib qaytaring",
] as const;
function calculateHeadPose(landmarks: any[]) {
  const mirrorX = (x: number) => 1 - x;

  const nose = landmarks[1];
  const leftEye = landmarks[33];
  const rightEye = landmarks[263];
  const leftMouth = landmarks[78];
  const rightMouth = landmarks[308];

  const mirNoseX = mirrorX(nose.x);
  const mirLeftEyeX = mirrorX(leftEye.x);
  const mirRightEyeX = mirrorX(rightEye.x);
  const mirLeftMouthX = mirrorX(leftMouth.x);
  const mirRightMouthX = mirrorX(rightMouth.x);

  const faceCenterX = (mirLeftEyeX + mirRightEyeX) / 2;

  const eyeDistance = Math.abs(mirLeftEyeX - mirRightEyeX);
  const mouthDistance = Math.abs(mirLeftMouthX - mirRightMouthX);

  // More sensitive detection for mobile devices
  const horizontalOffset =
    (mirNoseX - faceCenterX) / Math.max(eyeDistance, 0.1);
  const verticalOffset =
    (nose.y - (leftEye.y + rightEye.y) / 2) / Math.max(eyeDistance, 0.1);

  return {
    horizontal: horizontalOffset,
    vertical: verticalOffset,
    eyeDistance,
    mouthDistance,
  };
}

function isHeadTurnedLeft(pose: any) {
  // More sensitive detection for mobile
  const result = pose.horizontal <= -0.25;
  return result;
}

function isHeadTurnedRight(pose: any) {
  // More sensitive detection for mobile
  const result = pose.horizontal >= 0.25;
  return result;
}

function isHeadCentered(pose: any) {
  // More tolerant center detection for mobile
  console.log(pose);
  const result = pose.horizontal > -0.2 && pose.horizontal < 0.1;
  return result;
}

function areEyesOpen(blendshapes: any[]): boolean {
  if (!blendshapes || blendshapes.length === 0) return false;
  const categories = blendshapes[0]?.categories;
  if (!categories) return false;

  const leftBlink = categories.find(
    (c: any) => c.categoryName === "eyeBlinkLeft",
  );
  const rightBlink = categories.find(
    (c: any) => c.categoryName === "eyeBlinkRight",
  );

  const leftClosed = leftBlink && leftBlink.score > EYE_BLINK_THRESHOLD;
  const rightClosed = rightBlink && rightBlink.score > EYE_BLINK_THRESHOLD;

  return !leftClosed && !rightClosed;
}

const mirrorX = (x: number, width: number) => width - x;

export const FaceId = () => {
  const location = useLocation();
  const { user } = useAuthContext();
  const { data: attendanceId } = useGetAttendanceId();
  const { isPending: isCheckin, mutateAsync } = useSendCheckIn();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [faceInCircle, setFaceInCircle] = useState(false);
  const [livenessStep, setLivenessStep] = useState(0);
  const [status, setStatus] = useState("Yuzingizni doira ichiga olib kiring");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const faceLandmarkerRef = useRef<any>(null);
  const [faceHoldStart, setFaceHoldStart] = useState<number | null>(null);
  const hasSubmittedRef = useRef(false);
  const [checkInTime, setCheckInTime] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const [retryCount, setRetryCount] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);
  const [dimensions, setDimensions] = useState(getResponsiveDimensions());
  const maxRetries = 3;
  const recoverDetection = useCallback(async () => {
    if (isRecovering || retryCount >= maxRetries) return;

    setIsRecovering(true);

    try {
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
        faceLandmarkerRef.current = null;
      }

      await new Promise<void>((resolve) => {
        setTimeout(resolve, 1000);
      });
      const filesetResolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm",
      );
      const faceLandmarker = await FaceLandmarker.createFromOptions(
        filesetResolver,
        {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          },
          outputFaceBlendshapes: true,
          outputFacialTransformationMatrixes: true,
          numFaces: 1,
          runningMode: "VIDEO",
          minFaceDetectionConfidence: 0.5, // Lower confidence for better mobile detection
          minTrackingConfidence: 0.5, // Lower tracking confidence for mobile
        },
      );

      faceLandmarkerRef.current = faceLandmarker;
      setRetryCount((prev) => prev + 1);
      setIsRecovering(false);
    } catch (e: any) {
      setError(`Recovery failed: ${e?.message || e}`);
      setIsRecovering(false);
    }
  }, [isRecovering, retryCount, maxRetries]);

  const restartDetection = useCallback(() => {
    window.location.reload();
  }, []);

  const cleanup = useCallback(() => {
    if (faceLandmarkerRef.current) {
      try {
        faceLandmarkerRef.current.close();
      } catch (e) {
        return;
      }
      faceLandmarkerRef.current = null;
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setCurrentLocation({ latitude: 0, longitude: 0 });
    } else {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          setCurrentLocation({ latitude: lat, longitude: lon });
        },
        () => {
          setCurrentLocation({ latitude: 0, longitude: 0 });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    }
  }, []);

  // Handle window resize for responsive dimensions
  useEffect(() => {
    const handleResize = () => {
      setDimensions(getResponsiveDimensions());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let running = true;
    async function loadModel() {
      setLoading(true);

      try {
        const filesetResolver = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm",
        );
        const faceLandmarker = await FaceLandmarker.createFromOptions(
          filesetResolver,
          {
            baseOptions: {
              modelAssetPath:
                "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            },
            outputFaceBlendshapes: true,
            outputFacialTransformationMatrixes: true,
            numFaces: 1,
            runningMode: "VIDEO",
            minFaceDetectionConfidence: 0.5, // Lower confidence for better mobile detection
            minTrackingConfidence: 0.5, // Lower tracking confidence for mobile
          },
        );
        if (running) {
          faceLandmarkerRef.current = faceLandmarker;
          setLoading(false);

          setRetryCount(0);
        }
      } catch (e: any) {
        if (running) {
          setError(`Modellarni yuklab bo'lmadi: ${e?.message || e}`);
          setLoading(false);
          setTimeout(() => {
            if (running && retryCount < maxRetries) {
              recoverDetection();
            }
          }, 2000);
        }
      }
    }
    loadModel();
    return () => {
      running = false;
    };
  }, [retryCount, maxRetries, recoverDetection]);

  useEffect(() => {
    if (!videoRef.current) return;

    let stream: MediaStream | null = null;
    let retryTimeout: NodeJS.Timeout;

    const startWebcam = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: dimensions.videoWidth * 2, max: 1280 },
            height: { ideal: dimensions.videoHeight * 2, max: 720 },
            facingMode: "user",
            frameRate: { ideal: 30, max: 60 },
          },
        });

        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play().catch((e) => {
              setError(`Kamerani ishlatib bo'lmadi: ${e?.message || e}`);
            });
          };

          stream.getVideoTracks()[0].addEventListener("ended", () => {
            setError("Kameraga ulanib bo'lmadi. Qayta ulanish jarayonida...");
            retryTimeout = setTimeout(startWebcam, 2000);
          });
        }
      } catch (e: any) {
        setError(`Kamerada xatolik: ${e?.message || e}`);
        retryTimeout = setTimeout(startWebcam, 3000);
      }
    };

    startWebcam();

    // eslint-disable-next-line consistent-return
    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (stream) {
        stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    let raf: number;
    async function detect() {
      if (
        !faceLandmarkerRef.current ||
        !videoRef.current ||
        !canvasRef.current ||
        videoRef.current.readyState !== 4
      ) {
        raf = requestAnimationFrame(detect);
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        raf = requestAnimationFrame(detect);
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.strokeStyle = faceInCircle ? "#22c55e" : "#ef4444";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(
        dimensions.circleCenter.x,
        dimensions.circleCenter.y,
        dimensions.circleRadius,
        0,
        2 * Math.PI,
      );
      ctx.stroke();
      ctx.restore();
      let results = null;
      try {
        results = await faceLandmarkerRef.current.detectForVideo(
          video,
          performance.now(),
        );
      } catch (e: any) {
        if (retryCount < maxRetries) {
          setTimeout(() => recoverDetection(), 1000);
        } else {
          setError(
            `Aniqlashda xatolik: ${maxRetries} marta qayta urinishdan so'ng: ${e?.message || e}`,
          );
        }
        raf = requestAnimationFrame(detect);
        return;
      }
      if (
        results &&
        results.faceLandmarks &&
        results.faceLandmarks.length > 0
      ) {
        const landmarks = results.faceLandmarks[0];
        const eyesOpen = areEyesOpen(results.faceBlendshapes);
        ctx.save();
        ctx.fillStyle = "#3b82f6";
        landmarks.forEach((pt: any, index: number) => {
          // Highlight key facial points
          const isKeyPoint = [1, 33, 263, 78, 308].includes(index);
          ctx.beginPath();
          ctx.arc(
            mirrorX(pt.x * canvas.width, canvas.width),
            pt.y * canvas.height,
            isKeyPoint ? 4 : 2,
            0,
            2 * Math.PI,
          );
          ctx.fill();

          // Highlight nose point (index 1) with a different color
          if (index === 1) {
            ctx.fillStyle = "#ef4444";
            ctx.beginPath();
            ctx.arc(
              mirrorX(pt.x * canvas.width, canvas.width),
              pt.y * canvas.height,
              6,
              0,
              2 * Math.PI,
            );
            ctx.fill();
            ctx.fillStyle = "#3b82f6";
          }
        });
        ctx.restore();
        const nose = landmarks[1];
        const px = mirrorX(nose.x * canvas.width, canvas.width);
        const py = nose.y * canvas.height;

        // Calculate distance from nose to circle center
        const dist = Math.sqrt(
          (px - dimensions.circleCenter.x) ** 2 +
            (py - dimensions.circleCenter.y) ** 2,
        );

        // Use a more lenient threshold for face detection
        const inCircle = dist < dimensions.circleRadius * 0.8;
        setFaceInCircle(inCircle);

        if (!inCircle) {
          setStatus("Yuz doira ichida emas");
          if (livenessStep === 0) {
            setFaceHoldStart(null);
          }
        } else if (livenessStep === 0) {
          const now = Date.now();

          if (!faceHoldStart) {
            setFaceHoldStart(now);
            setStatus("Yuzingizni doira ichida 1 soniya ushlab turing");
          } else {
            const holdTimeElapsed = now - faceHoldStart;

            if (holdTimeElapsed > FACE_HOLD_TIME) {
              setStatus(LIVENESS_STEPS[0]);
              setLivenessStep(1);
              setFaceHoldStart(null);
            } else {
              const remainingTime = Math.max(
                0,
                FACE_HOLD_TIME - holdTimeElapsed,
              );
              setStatus(
                `Yuzingizni doira ichida ${Math.ceil(remainingTime / 1000)} soniya ushlab turing`,
              );
            }
          }
        } else if (livenessStep > 0 && livenessStep <= 3) {
          const headPose = calculateHeadPose(landmarks);
          let passed = false;

          if (livenessStep === 1) {
            passed = isHeadTurnedLeft(headPose);
          } else if (livenessStep === 2) {
            passed = isHeadTurnedRight(headPose);
          } else if (livenessStep === 3) {
            passed = isHeadCentered(headPose);
          }
          if (passed) {
            if (livenessStep === 1) {
              setLivenessStep(2);
              setStatus(LIVENESS_STEPS[1]); // "Boshni o'ngga buring"
            } else if (livenessStep === 2) {
              setLivenessStep(3);
              setStatus(LIVENESS_STEPS[2]); // "Boshni to'g'ri qilib qaytaring"
            } else if (
              livenessStep === 3 &&
              !hasSubmittedRef.current &&
              !eyesOpen
            ) {
              setStatus("Ko'zlaringizni oching");
            } else if (
              livenessStep === 3 &&
              !hasSubmittedRef.current &&
              eyesOpen
            ) {
              hasSubmittedRef.current = true;
              setIsSubmitting(true);
              await new Promise((resolve) => {
                setTimeout(resolve, 1000);
              });
              try {
                const photoBlob = await capturePhoto();
                if (photoBlob && currentLocation) {
                  const formData = new FormData();
                  formData.append("image", photoBlob, "face-photo.jpg");
                  formData.append("latitude", String(currentLocation.latitude));
                  formData.append(
                    "longitude",
                    String(currentLocation.longitude),
                  );
                  formData.append(
                    "offsite_attendance",
                    location.pathname.includes("off-site") ? "true" : "false",
                  );
                  if (user?.makhalla?.id) {
                    formData.append("makhalla_id", String(user.makhalla.id));
                  }

                  await mutateAsync(formData);

                  setCheckInTime(new Date().toLocaleTimeString("uz-UZ"));
                  setSuccess(true);
                  setIsSubmitting(false);
                } else {
                  setError(
                    "Rasm olishda xatolik. Iltimos qayta urinib ko'ring.",
                  );
                  setIsSubmitting(false);
                }
              } catch (checkInError: any) {
                setError(checkInError.response.data.detail);
                setIsSubmitting(false);
                setLivenessStep(0);
                setFaceHoldStart(null);
                hasSubmittedRef.current = false;
              }
            }
          }
        }
      } else {
        setFaceInCircle(false);
        setStatus("Yuz aniqlanmadi");
        if (livenessStep === 0) {
          setFaceHoldStart(null);
        }
      }
      raf = requestAnimationFrame(detect);
    }
    if (!loading && !error && !isSubmitting) detect();
    return () => cancelAnimationFrame(raf);
  }, [loading, error, livenessStep, faceHoldStart, isSubmitting, dimensions]);

  function capturePhoto(): Promise<File | null> {
    return new Promise((resolve) => {
      const video = videoRef.current;
      if (!video) {
        resolve(null);
        return;
      }

      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = video.videoWidth || dimensions.videoWidth;
      tempCanvas.height = video.videoHeight || dimensions.videoHeight;
      const tempCtx = tempCanvas.getContext("2d");

      if (tempCtx) {
        tempCtx.drawImage(video, 0, 0);
        tempCanvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null);
              return;
            }

            // Convert Blob -> File
            const file = new File([blob], "face-photo.jpg", {
              type: "image/jpeg",
              lastModified: Date.now(),
            });

            resolve(file);
          },
          "image/jpeg",
          0.8,
        );
      } else {
        resolve(null);
      }
    });
  }

  if (attendanceId?.is_face_id_verified)
    return (
      <Navigate
        to={
          location.pathname.includes("off-site")
            ? "/yetakchi/dashboard/attendance/off-site"
            : "/yetakchi/dashboard/attendance/in"
        }
      />
    );

  if (isCheckin) return <ContentSkeleton />;
  if (success && checkInTime)
    return <Navigate to="/yetakchi/dashboard/attendance/in" />;

  return (
    <div className="mt-4 flex flex-col items-center justify-center sm:mt-10 sm:px-0">
      <div className="relative w-full sm:w-auto">
        <video
          ref={videoRef}
          className="h-full w-full rounded-lg border-4 object-cover sm:w-auto"
          style={{
            transform: "scaleX(-1)",
            width: `${dimensions.videoWidth}px`,
            height: `${dimensions.videoHeight}px`,
            maxWidth: "100%",
            pointerEvents: "none",
          }}
          playsInline
          autoPlay
          muted
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
        />
        <canvas
          ref={canvasRef}
          width={dimensions.videoWidth}
          height={dimensions.videoHeight}
          className="pointer-events-none absolute left-0 top-0 h-full w-full"
        />
        {(loading || error || isRecovering || isSubmitting) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-60">
            {error ? (
              <div className="p-4 text-center">
                <div className="mb-4 text-lg font-bold text-red-500">
                  {error}
                </div>
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={restartDetection}
                    className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                  >
                    Qayta o&apos;tish
                  </button>
                </div>
              </div>
            ) : (
              <>
                {loading && (
                  <div className="text-center">
                    <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-white" />
                    <div className="text-white">Iltimos kuting...</div>
                  </div>
                )}
                {isRecovering && (
                  <div className="text-center">
                    <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-yellow-500" />
                    <div className="text-yellow-500">Qayta yuklanmoqda...</div>
                  </div>
                )}
                {isSubmitting && (
                  <div className="text-center">
                    <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-green-500" />
                    <div className="text-green-500">Rasm yuborilmoqda...</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <div className="mt-4 flex w-full max-w-[480px] flex-col items-center px-4 sm:mt-6 sm:px-0">
        {!error && (
          <div
            className={cn(
              "mb-2 rounded px-3 py-2 text-sm font-semibold sm:px-4 sm:text-lg",
              faceInCircle
                ? "bg-green-600 text-white"
                : "bg-red-600 text-white",
            )}
          >
            {status}
          </div>
        )}
        {!error && livenessStep > 0 && livenessStep <= 3 && (
          <div className="mt-2 h-3 w-full rounded-full bg-gray-700 sm:h-4">
            <div
              className="h-3 rounded-full bg-blue-500 transition-all duration-500 sm:h-4"
              style={{
                width: `${(livenessStep / 3) * 100}%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
